import { useEffect, useState } from "react";
import axios from "axios";
import { CAREER_ROLES, ASSESSMENT } from "@/data/program";
import { motion } from "framer-motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Fallback data while loading or if API is unavailable
const FALLBACK = {
  name: "Umashankar K H",
  title: "Lead Instructor",
  headline: "Data Scientist · GenAI & Agentic AI Systems",
  bio: 'Umashankar K H leads the program with a "Deploy first, understand deeply" philosophy. Every module is designed around real client scenarios he has worked through — with the trade-offs, failures and rollbacks that come with them.',
  photo_url: "https://images.pexels.com/photos/6325958/pexels-photo-6325958.jpeg",
  website: "https://www.umashankar.space",
  linkedin: "https://linkedin.com/in/umashankar-kh",
  github: "https://github.com/umashankar-kh",
};

export default function Instructor() {
  const [instructor, setInstructor] = useState(null);

  useEffect(() => {
    axios.get(`${API}/instructors`)
      .then((res) => { if (res.data?.length > 0) setInstructor(res.data[0]); })
      .catch(() => {}); // silently fall back
  }, []);

  const inst = instructor || FALLBACK;

  return (
    <section
      id="instructor"
      data-testid="instructor-section"
      className="bg-white relative"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16 items-start">
          {/* Portrait */}
          <div className="md:col-span-5">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.9 }}
              className="relative"
            >
              <img
                src={inst.photo_url || "https://images.pexels.com/photos/6325958/pexels-photo-6325958.jpeg"}
                alt={`${inst.name} — ${inst.headline || inst.title}`}
                className="w-full aspect-[4/5] object-cover grayscale"
              />
              <div className="absolute bottom-0 left-0 hidden md:block bg-slate-900/75 backdrop-blur-sm text-slate-50 p-6 max-w-[220px]">
                <p className="font-mono uppercase text-[10px] tracking-[0.24em] text-slate-400 mb-2">
                  {inst.title || "Lead Instructor"}
                </p>
                <p className="font-serif text-xl leading-tight">{inst.name}</p>
              </div>
            </motion.div>
          </div>

          {/* Bio + assessment */}
          <div className="md:col-span-7">
            <p className="font-mono uppercase tracking-[0.24em] text-xs text-slate-500 mb-6">
              — Instructor
            </p>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-tight text-slate-900">
              Learn from a practitioner who <em className="text-slate-500">has shipped</em>.
            </h2>
            <p className="mt-8 text-lg text-slate-600 font-sans leading-relaxed max-w-2xl">
              {inst.bio || 'Umashankar K H leads the program with a "Deploy first, understand deeply" philosophy. Every module is designed around real client scenarios he has worked through — with the trade-offs, failures and rollbacks that come with them.'}
            </p>
            {/* Links */}
            {(inst.website || inst.linkedin || inst.github) && (
              <div className="mt-6 flex gap-4">
                {inst.website && (
                  <a href={inst.website} target="_blank" rel="noopener noreferrer"
                    className="font-mono uppercase text-[10px] tracking-[0.2em] text-slate-500 hover:text-slate-900 underline underline-offset-4">
                    Website ↗
                  </a>
                )}
                {inst.linkedin && (
                  <a href={inst.linkedin} target="_blank" rel="noopener noreferrer"
                    className="font-mono uppercase text-[10px] tracking-[0.2em] text-slate-500 hover:text-slate-900 underline underline-offset-4">
                    LinkedIn ↗
                  </a>
                )}
                {inst.github && (
                  <a href={inst.github} target="_blank" rel="noopener noreferrer"
                    className="font-mono uppercase text-[10px] tracking-[0.2em] text-slate-500 hover:text-slate-900 underline underline-offset-4">
                    GitHub ↗
                  </a>
                )}
              </div>
            )}

            <div className="mt-12">
              <p className="font-mono uppercase text-xs tracking-[0.24em] text-slate-500 mb-6">
                Career outcomes
              </p>
              <div className="flex flex-wrap gap-2">
                {CAREER_ROLES.map((r) => (
                  <span
                    key={r}
                    data-testid={`career-role-${r.toLowerCase().replace(/\s+/g, "-")}`}
                    className="border border-slate-300 text-slate-800 px-4 py-2 text-sm font-sans hover:border-slate-900 hover:bg-slate-50 transition-colors"
                  >
                    {r}
                  </span>
                ))}
              </div>
            </div>

            <div className="mt-16 pt-12 border-t border-slate-200">
              <p className="font-mono uppercase text-xs tracking-[0.24em] text-slate-500 mb-8">
                Assessment · Certificate: FDE AI Industry Practitioner
              </p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
                {ASSESSMENT.map((a) => (
                  <div key={a.label} data-testid={`assessment-${a.label.toLowerCase().replace(/\s+/g, "-")}`}>
                    <p className="font-serif text-5xl text-slate-900 tracking-tight">
                      {a.pct}<span className="text-slate-400 text-3xl align-top">%</span>
                    </p>
                    <p className="mt-2 text-xs font-mono uppercase tracking-widest text-slate-500">
                      {a.label}
                    </p>
                  </div>
                ))}
              </div>
              <p className="mt-8 text-sm text-slate-600 font-sans">
                Minimum 70% overall required to receive the FDE AI Industry Practitioner certificate.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
